import { voyagerTrack } from '@shared/voyager';

export const LOGGER_EVENT = 'crm_fe_log';
export const LOGGER_VERSION = '1.0';

export type LoggerLevel = 'debug' | 'info' | 'warn' | 'error';
export type LoggerStatus =
    | 'start'
    | 'success'
    | 'failure'
    | 'timeout'
    | 'retry';
export type LoggerScalar = string | number | boolean | null | undefined;

export interface LoggerInput {
    module: string;
    action: string;
    level?: LoggerLevel;
    status?: LoggerStatus;
    source?: string;
    traceId?: string;
    costMs?: number;
    errorCode?: string | number;
    errorMessage?: string;
    tags?: Record<string, LoggerScalar>;
    metrics?: Record<string, number | undefined>;
    extra?: Record<string, unknown>;
    sampleRate?: number;
}

export interface LoggerContext {
    /** CRM 登录账号（邮箱前缀），不等同于 Voyager/Octopus 的 uid。 */
    crmUserId?: string;
}

const SENSITIVE_KEY_PATTERN = /(^|_)(access_key|api_key|authorization|cookie|credentials?|mobile|password|phone|private_key|secret|session_id|session_key|tel|telephone|token)($|_)/;
const SENSITIVE_VALUE_KEY_SOURCE =
    'access[_-]?(?:token|key)|refresh[_-]?token|api[_-]?key|client[_-]?secret|session[_-]?(?:id|token|credential)|credential(?:s)?|private[_-]?key|secret[_-]?access[_-]?key|token|authorization|cookie|password|secret';
const SENSITIVE_ASSIGNMENT_PATTERN = new RegExp(
    `\\b(${SENSITIVE_VALUE_KEY_SOURCE})\\b\\s*[:=]\\s*[^\\s,;&]+`,
    'gi',
);
const SENSITIVE_JSON_ASSIGNMENT_PATTERN = new RegExp(
    `(["'])(${SENSITIVE_VALUE_KEY_SOURCE})\\1\\s*:\\s*(["'])(?:\\\\.|(?!\\3)[\\s\\S])*?(?:\\3|$)`,
    'gi',
);
const SENSITIVE_JSON_PRIMITIVE_PATTERN = new RegExp(
    `(["'])(${SENSITIVE_VALUE_KEY_SOURCE})\\1\\s*:\\s*(?!["'])[^\\s,}\\]]+`,
    'gi',
);
const EXTRA_JSON_MAX_LENGTH = 2000;
const EXTRA_NODE_MAX_COUNT = 300;
const DYNAMIC_FIELD_MAX_COUNT = 30;
const TEXT_MAX_LENGTH = 500;
let loggerContext: LoggerContext = {};

interface SanitizeBudget {
    remainingNodes: number;
}

const truncate = (value: string, maxLength: number) => {
    return value.length > maxLength ? value.slice(0, maxLength) : value;
};

const sanitizeText = (value: string) => {
    return truncate(value, TEXT_MAX_LENGTH)
        .replace(
            /-----BEGIN(?: [A-Z]+)? PRIVATE KEY-----[\s\S]*/gi,
            '[REDACTED_PRIVATE_KEY]',
        )
        .replace(
            /\bAuthorization\s*:\s*(?:Basic|Bearer)\s+[a-zA-Z0-9._~+/=-]+/gi,
            'Authorization: [REDACTED]',
        )
        .replace(
            /\b(?:Set-Cookie|Cookie)\s*:\s*[^\r\n]*/gi,
            'Cookie: [REDACTED]',
        )
        .replace(/\bBearer\s+[a-zA-Z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
        .replace(SENSITIVE_JSON_ASSIGNMENT_PATTERN, '$1$2$1:$3[REDACTED]$3')
        .replace(SENSITIVE_JSON_PRIMITIVE_PATTERN, '$1$2$1:"[REDACTED]"')
        .replace(SENSITIVE_ASSIGNMENT_PATTERN, '$1=[REDACTED]')
        .replace(
            /(^|[^\d])(?:\+?86[- ]?)?1[3-9]\d{9}(?!\d)/g,
            '$1[REDACTED_PHONE]',
        );
};

const normalizeFieldName = (value: string) => {
    return value
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/[^a-zA-Z0-9_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .toLowerCase();
};

const isSensitiveKey = (key: string) => {
    return SENSITIVE_KEY_PATTERN.test(normalizeFieldName(key));
};

const takeOwnKeys = (value: object, maxCount: number) => {
    const keys: string[] = [];
    for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
            keys.push(key);
            if (keys.length >= maxCount) {
                break;
            }
        }
    }
    return keys;
};

const sanitizeExtra = (
    value: unknown,
    seen: unknown[],
    budget: SanitizeBudget,
    depth = 0,
): unknown => {
    if (budget.remainingNodes <= 0) {
        return '[TRUNCATED]';
    }
    budget.remainingNodes -= 1;
    if (depth > 4) {
        return '[MAX_DEPTH]';
    }
    if (
        value === null ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        return value;
    }
    if (typeof value === 'string') {
        return sanitizeText(value);
    }
    if (typeof value === 'undefined') {
        return undefined;
    }
    if (value instanceof Error) {
        return {
            name: value.name,
            message: sanitizeText(value.message),
        };
    }
    if (typeof value !== 'object') {
        return String(value);
    }
    if (seen.includes(value)) {
        return '[CIRCULAR]';
    }

    seen.push(value);
    if (Array.isArray(value)) {
        return value
            .slice(0, 20)
            .map(item => sanitizeExtra(item, seen, budget, depth + 1));
    }

    const sanitized: Record<string, unknown> = {};
    takeOwnKeys(value as Record<string, unknown>, 50).forEach(key => {
        sanitized[key] = isSensitiveKey(key)
            ? '[REDACTED]'
            : sanitizeExtra(
                  (value as Record<string, unknown>)[key],
                  seen,
                  budget,
                  depth + 1,
              );
    });
    return sanitized;
};

const stringifyExtra = (extra?: Record<string, unknown>) => {
    if (!extra) {
        return undefined;
    }
    try {
        const serialized = JSON.stringify(
            sanitizeExtra(extra, [], {
                remainingNodes: EXTRA_NODE_MAX_COUNT,
            }),
        );
        if (serialized.length <= EXTRA_JSON_MAX_LENGTH) {
            return serialized;
        }
        return JSON.stringify({
            truncated: true,
            preview: serialized.slice(0, EXTRA_JSON_MAX_LENGTH - 100),
        });
    } catch (error) {
        return JSON.stringify({ serializationError: true });
    }
};

const buildDynamicFields = (
    prefix: 'tag' | 'metric',
    values?: Record<string, LoggerScalar>,
) => {
    const fields: Record<string, LoggerScalar> = {};
    if (!values) {
        return fields;
    }

    const keys = takeOwnKeys(values, DYNAMIC_FIELD_MAX_COUNT + 1);
    keys.slice(0, DYNAMIC_FIELD_MAX_COUNT).forEach(key => {
        if (isSensitiveKey(key)) {
            return;
        }
        const fieldName = normalizeFieldName(key);
        const value = values[key];
        if (!fieldName || typeof value === 'undefined') {
            return;
        }
        fields[`${prefix}_${fieldName}`] =
            typeof value === 'string' ? sanitizeText(value) : value;
    });
    if (keys.length > DYNAMIC_FIELD_MAX_COUNT) {
        fields[`${prefix}_fields_truncated`] = prefix === 'metric' ? 1 : true;
    }
    return fields;
};

export const createLogTraceId = (prefix = 'crm') => {
    const random = Math.random()
        .toString(36)
        .slice(2, 10);
    return `${normalizeFieldName(prefix) || 'crm'}_${Date.now().toString(
        36,
    )}_${random}`;
};

export const parseLogError = (error: unknown) => {
    if (error instanceof Error) {
        const errorWithCode = error as Error & { code?: string | number };
        return {
            errorCode: errorWithCode.code,
            errorMessage: error.message,
        };
    }
    return {
        errorCode: undefined,
        errorMessage:
            typeof error === 'string' ? error : 'Unknown frontend error',
    };
};

/** 登录信息加载后设置一次，后续日志会自动携带公共上下文。 */
export const setLoggerContext = (context: LoggerContext) => {
    loggerContext = { ...context };
};

/**
 * CRM 前端技术诊断日志。业务埋点继续使用独立 Voyager 事件。
 * 使用规范见 docs/frontend-logging.md。
 */
export const logger = (input: LoggerInput) => {
    try {
        const sampleRate = Math.max(0, Math.min(input.sampleRate ?? 1, 1));
        if (
            sampleRate <= 0 ||
            (sampleRate < 1 && Math.random() >= sampleRate)
        ) {
            return;
        }

        const payload = {
            log_version: LOGGER_VERSION,
            log_module: normalizeFieldName(input.module),
            log_action: normalizeFieldName(input.action),
            log_level: input.level || 'info',
            log_status:
                input.status ||
                (input.level === 'error' ||
                typeof input.errorCode !== 'undefined' ||
                !!input.errorMessage
                    ? 'failure'
                    : 'success'),
            log_source: input.source
                ? normalizeFieldName(input.source)
                : undefined,
            crm_user_id:
                typeof loggerContext.crmUserId !== 'undefined'
                    ? sanitizeText(String(loggerContext.crmUserId))
                    : undefined,
            trace_id: input.traceId,
            cost_ms:
                typeof input.costMs === 'number'
                    ? Math.max(0, Math.round(input.costMs))
                    : undefined,
            error_code:
                typeof input.errorCode !== 'undefined'
                    ? String(input.errorCode)
                    : undefined,
            error_message: input.errorMessage
                ? sanitizeText(input.errorMessage)
                : undefined,
            app_env:
                process.env.REACT_APP_ENV || process.env.NODE_ENV || 'unknown',
            page_path:
                typeof window !== 'undefined' ? window.location.pathname : '',
            browser_online:
                typeof navigator !== 'undefined' ? navigator.onLine : undefined,
            visibility_state:
                typeof document !== 'undefined'
                    ? document.visibilityState
                    : undefined,
            ...buildDynamicFields('tag', input.tags),
            ...buildDynamicFields('metric', input.metrics),
            extra_json: stringifyExtra(input.extra),
        };

        const trackResult = voyagerTrack(LOGGER_EVENT, payload);
        if (trackResult && typeof trackResult.catch === 'function') {
            trackResult.catch(() => undefined);
        }
    } catch (error) {
        // 日志上报不能影响业务流程
    }
};
